import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { CanAccess, useUserMenu } from "ra-core";
import { Search, Settings, User, Users, Import } from "lucide-react";
import { Button } from "@/components/ui/button";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

const UsersMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users className="w-4 h-4" /> Users
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User className="w-4 h-4" /> Profile
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings className="w-4 h-4" /> Settings
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const userMenuContext = useUserMenu();
  if (!userMenuContext) return null;
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/import" className="flex items-center gap-2">
        <Import className="w-4 h-4" /> Import data
      </Link>
    </DropdownMenuItem>
  );
};

const searchItems = [
  // CRM
  { label: "Leads / Contacts", category: "CRM", to: "/contacts" },
  { label: "Enquiries", category: "CRM", to: "/enquiries" },
  { label: "Opportunities", category: "CRM", to: "/opportunities" },
  { label: "Projects", category: "CRM", to: "/projects" },
  { label: "Companies", category: "CRM", to: "/companies" },
  // Sales
  { label: "Global Ledger (Sales)", category: "Sales", to: "/sales" },
  { label: "Auto Invoice Queue", category: "Sales", to: "/dispatch_to_invoice_queue" },
  { label: "Customer Aging", category: "Sales", to: "/customer_aging_view" },
  { label: "Credit Limit Management", category: "Sales", to: "/credit-limits" },
  { label: "PDC Tracking", category: "Sales", to: "/pdc_cheques" },
  { label: "Credit Notes", category: "Sales", to: "/credit_notes" },
  { label: "Receipt Matching", category: "Sales", to: "/payment_receipts" },
  // Documents
  { label: "Quotations", category: "Documents", to: "/quotations" },
  { label: "Sales Orders", category: "Documents", to: "/sales_orders" },
  { label: "Purchase Orders", category: "Documents", to: "/purchase_orders" },
  { label: "Proforma Invoices", category: "Documents", to: "/proforma_invoices" },
  { label: "Customer Invoices", category: "Documents", to: "/customer_invoices" },
  { label: "Delivery Challans", category: "Documents", to: "/delivery_challans" },
  // Design & Production
  { label: "Design Requests", category: "Design & Production", to: "/design_requests" },
  { label: "Bill of Materials (BOM)", category: "Design & Production", to: "/boms" },
  { label: "RFQs", category: "Design & Production", to: "/rfqs" },
  { label: "Work Orders", category: "Design & Production", to: "/work_orders" },
  // Operations
  { label: "Inventory Items", category: "Operations", to: "/inventory_items" },
  { label: "HSN / SAC Codes", category: "Operations", to: "/hsn_codes" },
  { label: "Service Tickets", category: "Operations", to: "/service_tickets" },
  // Procurement
  { label: "Vendors List", category: "Procurement", to: "/vendors" },
  { label: "Goods Receipts (GRN)", category: "Procurement", to: "/goods_receipts" },
  { label: "Vendor Bills", category: "Procurement", to: "/vendor_bills" },
  // HR
  { label: "Employees", category: "HR", to: "/employees" },
  { label: "Departments", category: "HR", to: "/hr_departments" },
  { label: "Designations", category: "HR", to: "/hr_designations" },
  { label: "Attendance", category: "HR", to: "/hr_attendance" },
  { label: "Leave Requests", category: "HR", to: "/hr_leave_requests" },
  { label: "Holidays", category: "HR", to: "/hr_holidays" },
  { label: "Payroll Runs", category: "HR", to: "/hr_payroll_runs" },
  // Administration
  { label: "Users Management", category: "Administration", to: "/users" },
  { label: "Roles & Access Control", category: "Administration", to: "/user-roles" },
  { label: "System Permissions", category: "Administration", to: "/permissions" }
];

export const Header = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        setOpen(true);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleSelect = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  // Group search items by category
  const groupedItems = searchItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, typeof searchItems>);

  return (
    <header className="h-12 border-b border-slate-200 bg-white flex items-center justify-between px-6 shrink-0 shadow-xs">
      <div className="flex-1 max-w-sm">
        <button
          onClick={() => setOpen(true)}
          className="w-full flex items-center justify-between gap-2 px-3 py-1 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md hover:bg-slate-100/80 hover:text-slate-800 transition-all cursor-pointer shadow-2xs group"
        >
          <div className="flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600 transition-colors" />
            <span className="font-medium">Search modules...</span>
          </div>
          <kbd className="pointer-events-none inline-flex h-4.5 select-none items-center gap-0.5 rounded border border-slate-200 bg-white px-1 font-mono text-[9px] font-medium text-slate-400 shadow-2xs">
            <span>⌘</span>K
          </kbd>
        </button>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5 pl-3">
          <Button asChild variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-900 rounded-md">
            <Link to="/settings" title="Settings">
              <Settings className="w-4 h-4" />
            </Link>
          </Button>

          <UserMenu>
            <ProfileMenu />
            <CanAccess resource="sales" action="list">
              <UsersMenu />
            </CanAccess>
            <CanAccess resource="configuration" action="edit">
              <SettingsMenu />
            </CanAccess>
            <ImportFromJsonMenuItem />
          </UserMenu>
        </div>
      </div>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Type a module name to search..." />
        <CommandList>
          <CommandEmpty>No modules found.</CommandEmpty>
          {Object.entries(groupedItems).map(([category, items]) => (
            <CommandGroup key={category} heading={category}>
              {items.map((item) => (
                <CommandItem
                  key={item.to}
                  onSelect={() => handleSelect(item.to)}
                  className="cursor-pointer flex items-center justify-between py-2 px-3 hover:bg-slate-100 rounded-md transition-colors"
                >
                  <span className="font-medium text-slate-800">{item.label}</span>
                  <span className="text-xs text-slate-400 font-mono bg-slate-100 px-2 py-0.5 rounded border">
                    {item.to}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </header>
  );
};
